const CLARITY_ERROR_MESSAGE_FRAGMENT = "reading 'startTime'";
const CLARITY_ERROR_STACK_FRAGMENT = 'reportAllChanges';
const OPAQUE_ERROR_MESSAGE = 'Script error.';
// How long after a clarity('set','page',...) call to still treat an opaque error as Clarity's -
// covers the delayed/debounced ("n.timeout" in its stack) reportAllChanges call the bug is in.
const OPAQUE_ERROR_GRACE_PERIOD_MS = 5000;

let lastClarityPageChangeAt = 0;

/**
 * Call this whenever ClarityService actually sends `clarity('set', 'page', url)` - that call is
 * what re-arms Clarity's internal (buggy) web-vitals-style tracking for the "new page" and is
 * what the opaque-error grace period below is timed against.
 */
export function markClarityPageChange(): void {
  lastClarityPageChangeAt = Date.now();
}

/**
 * Microsoft Clarity's tag (loaded live from clarity.ms in ClarityService, not vendored) loads a
 * second script (scripts.clarity.ms/.../clarity.js) that has an internal bug throwing an
 * uncaught TypeError on some SPA route changes. It runs outside Angular's zone, so it never
 * reaches GlobalErrorHandler or Sentry - it only spams the browser console.
 *
 * That second script is inserted by Clarity's own loader, not by us, so we can't control whether
 * it's loaded with CORS enabled - depending on that, the browser either exposes the real
 * message/stack (matched directly below) or sanitizes it to the generic cross-origin-opaque
 * "Script error." with no filename/stack (matched only within a short window after we told
 * Clarity a page changed, so this doesn't swallow an unrelated opaque error at a random time).
 */
export function suppressClarityConsoleErrors(): void {
  window.addEventListener(
    'error',
    (event) => {
      const stack = event.error instanceof Error ? event.error.stack : undefined;
      const isClaritySpecificError =
        event.message?.includes(CLARITY_ERROR_MESSAGE_FRAGMENT) && stack?.includes(CLARITY_ERROR_STACK_FRAGMENT);
      const isOpaqueErrorNearClarityPageChange =
        event.message === OPAQUE_ERROR_MESSAGE && Date.now() - lastClarityPageChangeAt < OPAQUE_ERROR_GRACE_PERIOD_MS;

      if (isClaritySpecificError || isOpaqueErrorNearClarityPageChange) {
        event.preventDefault();
      }
    },
    true,
  );
}
